import { DBSchema, IDBPDatabase, openDB } from 'idb'

import type { LogEntry, SessionSnapshot } from './logger.types'

interface AppLoggerDB extends DBSchema {
  entries: {
    key: string
    value: LogEntry
    indexes: { timestamp: number; sessionId: string }
  }
  sessions: {
    key: string
    value: SessionSnapshot
  }
}

type DB = IDBPDatabase<AppLoggerDB>

const DB_NAME = 'app-logger'
const STORE = 'entries' as const
const SESSION_STORE = 'sessions' as const
const DB_VERSION = 2

function openAppDB(
  onError?: (context: string, error: unknown) => void
): Promise<DB> {
  return openDB<AppLoggerDB>(DB_NAME, DB_VERSION, {
    upgrade(db, _oldVersion, _newVersion, transaction) {
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('timestamp', 'timestamp')
        store.createIndex('sessionId', 'sessionId')
      } else {
        const store = transaction.objectStore(STORE)
        if (!store.indexNames.contains('sessionId')) {
          store.createIndex('sessionId', 'sessionId')
        }
      }
      if (!db.objectStoreNames.contains(SESSION_STORE)) {
        db.createObjectStore(SESSION_STORE, { keyPath: 'sessionId' })
      }
    },
    blocked: () =>
      onError?.('blocked', new Error('IndexedDB upgrade is blocked')),
    blocking: () =>
      onError?.(
        'blocking',
        new Error('Closing an outdated IndexedDB connection')
      ),
    terminated: () =>
      onError?.('terminated', new Error('IndexedDB connection terminated'))
  })
}

export class LoggerDB {
  private db: Promise<DB>
  private readonly onError?: (context: string, error: unknown) => void

  constructor(onError?: (context: string, error: unknown) => void) {
    this.onError = onError
    this.db = openAppDB(onError)
  }

  private async withDB<T>(fn: (db: DB) => Promise<T>): Promise<T> {
    try {
      return await fn(await this.db)
    } catch (e) {
      if (e instanceof DOMException && e.name === 'InvalidStateError') {
        this.db = openAppDB(this.onError)
        return fn(await this.db)
      }
      this.onError?.('operation', e)
      throw e
    }
  }

  async write(entry: LogEntry): Promise<void> {
    await this.withDB(db => db.add(STORE, entry))
  }

  async readAll(): Promise<LogEntry[]> {
    return this.withDB(db => db.getAll(STORE))
  }

  async writeSession(snapshot: SessionSnapshot): Promise<void> {
    await this.withDB(db => db.put(SESSION_STORE, snapshot))
  }

  async readSessions(): Promise<SessionSnapshot[]> {
    return this.withDB(db => db.getAll(SESSION_STORE))
  }

  async prune(maxAgeMs: number): Promise<void> {
    await this.withDB(async db => {
      const cutoff = Date.now() - maxAgeMs
      const tx = db.transaction(STORE, 'readwrite')
      let cursor = await tx.store
        .index('timestamp')
        .openCursor(IDBKeyRange.upperBound(cutoff))
      // we can't have multiple cursors at a time, no other solution than sequential deletes
      while (cursor) {
        // eslint-disable-next-line no-await-in-loop
        await cursor.delete()
        // eslint-disable-next-line no-await-in-loop
        cursor = await cursor.continue()
      }
      await tx.done

      const sessions = await db.getAll(SESSION_STORE)
      const expiredSessions = sessions.filter(
        session => session.endedAt != null && session.endedAt <= cutoff
      )
      if (expiredSessions.length > 0) {
        const sessionTx = db.transaction(SESSION_STORE, 'readwrite')
        for (const session of expiredSessions) {
          // eslint-disable-next-line no-await-in-loop
          await sessionTx.store.delete(session.sessionId)
        }
        await sessionTx.done
      }
    })
  }

  async clear(): Promise<void> {
    await this.withDB(async db => {
      const tx = db.transaction([STORE, SESSION_STORE], 'readwrite')
      await Promise.all([
        tx.objectStore(STORE).clear(),
        tx.objectStore(SESSION_STORE).clear()
      ])
      await tx.done
    })
  }

  async count(): Promise<number> {
    return this.withDB(db => db.count(STORE))
  }

  async enforceLimits(maxEntries: number, maxBytes: number): Promise<void> {
    await this.withDB(async db => {
      const entries = await db.getAllFromIndex(STORE, 'timestamp')
      let totalBytes = entries.reduce(
        (sum, entry) => sum + JSON.stringify(entry).length,
        0
      )
      let excessCount = Math.max(0, entries.length - maxEntries)
      if (excessCount === 0 && totalBytes <= maxBytes) return

      const tx = db.transaction(STORE, 'readwrite')
      for (const entry of entries) {
        if (excessCount <= 0 && totalBytes <= maxBytes) break
        // eslint-disable-next-line no-await-in-loop
        await tx.store.delete(entry.id)
        totalBytes -= JSON.stringify(entry).length
        excessCount -= 1
      }
      await tx.done
    })
  }

  static devWarn(context: string, error: unknown): void {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[LoggerDB] ${context}:`, error)
    }
  }
}
